import type { AdMediaType } from '../../types/ads';
import {
  ActivityKindEnum,
  FileKindEnum,
  PropertyKindEnum,
  RentPeriodEnum,
  TransactionTypeEnum,
} from '../../types/enums';
import type { PropertyAdEditorFormValues } from '../../types/propertyAdEditor';
import { priceFormat } from '../../utils';

import { PROPERTY_AD_EDITOR_PROPERTY_KIND_OPTIONS } from './propertyAdPropertyKinds';
import { getPropertySpecificationCompatibility } from './propertyAdSpecifications';

type PreviewPriceValues = Pick<
  PropertyAdEditorFormValues,
  | 'activityKind'
  | 'currency'
  | 'defaultRentFrequency'
  | 'price'
  | 'priceDaily'
  | 'priceMonthly'
  | 'priceWeekly'
  | 'priceYearly'
  | 'sellingPrice'
  | 'transactionType'
>;

type PreviewLocationValues = Pick<
  PropertyAdEditorFormValues,
  'addressEn' | 'buildingName' | 'nameEn' | 'propertyKind' | 'title'
>;

type PreviewSpecificationValues = Pick<
  PropertyAdEditorFormValues,
  'baths' | 'beds' | 'builtUpArea' | 'propertyKind'
>;

export interface PropertyAdPreviewPrice {
  amount: number;
  currency: PropertyAdEditorFormValues['currency'];
  period: string | null;
}

export interface PropertyAdPreviewSpec {
  kind: 'area' | 'beds' | 'baths';
  value: string;
}

export interface PropertyAdPreviewMedia {
  coverUrl: string | null;
  imageCount: number;
}

const preferredTitleKinds = new Set<PropertyKindEnum>([
  PropertyKindEnum.Shop,
  PropertyKindEnum.Office,
  PropertyKindEnum.Warehouse,
  PropertyKindEnum.ResidentialLand,
  PropertyKindEnum.IndustrialLand,
  PropertyKindEnum.CommercialLand,
]);

const rentFrequencyLabels: Record<RentPeriodEnum, string> = {
  [RentPeriodEnum.Yearly]: 'Yearly',
  [RentPeriodEnum.Monthly]: 'Monthly',
  [RentPeriodEnum.Weekly]: 'Weekly',
  [RentPeriodEnum.Daily]: 'Daily',
};

const normalizeText = (value: string | null | undefined): string | null => {
  const normalizedValue = value?.trim();

  return normalizedValue || null;
};

const isDisplayPrice = (value: number | null | undefined): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

export const getPropertyAdPreviewPrice = (
  values: PreviewPriceValues
): PropertyAdPreviewPrice | null => {
  if (values.activityKind === ActivityKindEnum.Sale) {
    const amount =
      values.transactionType === TransactionTypeEnum.Distress
        ? values.sellingPrice
        : values.transactionType === TransactionTypeEnum.Ready ||
            values.transactionType === TransactionTypeEnum.Resale
          ? values.price
          : null;

    return isDisplayPrice(amount) ? { amount, currency: values.currency, period: null } : null;
  }

  if (values.activityKind !== ActivityKindEnum.Rent || !values.defaultRentFrequency) {
    return null;
  }

  const rentPriceByPeriod: Record<RentPeriodEnum, number | null> = {
    [RentPeriodEnum.Yearly]: values.priceYearly,
    [RentPeriodEnum.Monthly]: values.priceMonthly,
    [RentPeriodEnum.Weekly]: values.priceWeekly,
    [RentPeriodEnum.Daily]: values.priceDaily,
  };
  const amount = rentPriceByPeriod[values.defaultRentFrequency];

  return isDisplayPrice(amount)
    ? {
        amount,
        currency: values.currency,
        period: rentFrequencyLabels[values.defaultRentFrequency],
      }
    : null;
};

export const getPropertyAdPreviewPropertyKind = (
  propertyKind: PropertyAdEditorFormValues['propertyKind']
): string | null =>
  PROPERTY_AD_EDITOR_PROPERTY_KIND_OPTIONS.find(({ value }) => value === propertyKind)?.label ??
  null;

export const getPropertyAdPreviewTitle = (values: PreviewLocationValues): string | null => {
  const title =
    normalizeText(values.nameEn) ??
    normalizeText(values.title) ??
    normalizeText(values.buildingName);

  if (!title) return null;

  const propertyKind = getPropertyAdPreviewPropertyKind(values.propertyKind);

  return values.propertyKind && preferredTitleKinds.has(values.propertyKind) && propertyKind
    ? `${propertyKind} at ${title}`
    : title;
};

export const getPropertyAdPreviewLocation = (values: PreviewLocationValues): string | null => {
  const address = normalizeText(values.addressEn);
  const title =
    normalizeText(values.nameEn) ??
    normalizeText(values.title) ??
    normalizeText(values.buildingName);

  if (!address) return null;

  return address.localeCompare(title ?? '', undefined, { sensitivity: 'accent' }) === 0
    ? null
    : address;
};

export const getPropertyAdPreviewSpecs = (
  values: PreviewSpecificationValues
): PropertyAdPreviewSpec[] => {
  const compatibility = getPropertySpecificationCompatibility(values.propertyKind);
  const specs: PropertyAdPreviewSpec[] = [];

  if (compatibility.builtUpArea && isDisplayPrice(values.builtUpArea)) {
    specs.push({ kind: 'area', value: `${priceFormat(values.builtUpArea)} sqft` });
  }

  if (compatibility.beds && values.beds) {
    specs.push({
      kind: 'beds',
      value:
        values.beds === 'Studio'
          ? values.beds
          : `${values.beds} ${Number(values.beds) === 1 ? 'bed' : 'beds'}`,
    });
  }

  if (compatibility.baths && values.baths) {
    specs.push({
      kind: 'baths',
      value: `${values.baths} ${Number(values.baths) === 1 ? 'bath' : 'baths'}`,
    });
  }

  return specs;
};

export const getPropertyAdPreviewMedia = (
  media: AdMediaType[] | null | undefined
): PropertyAdPreviewMedia => {
  const images = (media ?? [])
    .filter((item) => item.kind === FileKindEnum.Image)
    .sort((first, second) => first.order - second.order);

  return {
    coverUrl: images.find(({ fileUrl }) => Boolean(fileUrl))?.fileUrl ?? null,
    imageCount: images.length,
  };
};
